import React, { useState, useEffect, useCallback } from 'react';
import { useConfirmAlert } from './hooks/useConfirmAlert';
import { FiSave, FiDatabase } from 'react-icons/fi';
import { fetchPlanNames } from './api';

const DatabaseSaveDialog = ({
  show,
  onClose,
  onSave,
  initialName = '',
  initialDescription = '',
  isUpdate = false,
  userId,
  currentPlanName
}) => {
  const [planName, setPlanName] = useState(initialName);
  const [planDescription, setPlanDescription] = useState(initialDescription);
  const { alert } = useConfirmAlert();
  const [planNames, setPlanNames] = useState([]);
  const [selectedExistingPlan, setSelectedExistingPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNameWarning, setShowNameWarning] = useState(false);
  const [enableTimeShift, setEnableTimeShift] = useState(false);
  const [timeShiftStartDate, setTimeShiftStartDate] = useState('');
  const [timeShiftTargetDate, setTimeShiftTargetDate] = useState('');

  const loadPlanNames = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchPlanNames(userId);
      if (result.success) {
        setPlanNames(result.planNames);
        
        // Automatische Auswahl direkt nach dem Laden der Plan-Namen
        if (currentPlanName && result.planNames.length > 0) {
          const matchingPlan = result.planNames.find(name => name === currentPlanName);
          if (matchingPlan) {
            setSelectedExistingPlan(matchingPlan);
            setPlanName(''); // Leeren, da ein bestehender Plan ausgewählt wurde
          } else {
            // Wenn kein passender Plan gefunden wird, setze den Namen als neuen Plan-Namen
            // Aber nicht wenn es "unbenannt" ist - dann leer lassen für bessere UX
            setSelectedExistingPlan('');
            setPlanName(currentPlanName === 'unbenannt' ? '' : currentPlanName);
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Plan-Namen:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, currentPlanName]);

  useEffect(() => {
    if (show) {
      // Wenn initialName "unbenannt" ist, setze leeren String fuer bessere UX
      setPlanName(initialName === 'unbenannt' ? '' : initialName);
      setPlanDescription(initialDescription);
      setSelectedExistingPlan('');
      setEnableTimeShift(false);
      setTimeShiftStartDate('');
      setTimeShiftTargetDate('');
      if (!isUpdate) {
        loadPlanNames();
      }
    }
  }, [show, initialName, initialDescription, isUpdate, loadPlanNames]);

  if (!show) return null;

  const handleSave = async () => {
    const finalPlanName = selectedExistingPlan || planName;
    if (!finalPlanName.trim()) {
      await alert('Bitte geben Sie einen Plan-Namen ein oder waehlen Sie einen bestehenden Plan aus.', { title: "Eingabe erforderlich" });
      return;
    }
    
    // Verhindere das Speichern mit "unbenannt" als Planname
    if (finalPlanName.trim().toLowerCase() === 'unbenannt') {
      await alert('Der Name "unbenannt" ist reserviert und kann nicht gespeichert werden.\n\nBitte waehlen Sie einen aussagekraeftigen Namen fuer Ihren Plan (z.B. "Mathematik Semester 1").', { title: "Name nicht erlaubt" });
      return;
    }
    
    // Zeitverschiebung nur bei neuem Plan (nicht bei neuer Version)
    const timeShiftData = (!isUpdate && !selectedExistingPlan && enableTimeShift && timeShiftStartDate && timeShiftTargetDate)
      ? { startDate: timeShiftStartDate, targetDate: timeShiftTargetDate }
      : null;
    
    onSave(finalPlanName, planDescription, selectedExistingPlan, timeShiftData);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1002
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '500px',
        maxWidth: '90vw'
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '18px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          color: '#2196f3' 
        }}>
          <FiDatabase />
          {isUpdate ? 'Plan aktualisieren' : 'Plan in Datenbank speichern'}
        </h3>
        
        <p style={{ 
          margin: '0 0 20px 0', 
          fontSize: '14px', 
          color: '#666', 
          lineHeight: '1.5' 
        }}>
          
        </p>

        {/* Bestehender Plan auswählen (nur bei neuem Plan) */}
        {!isUpdate && planNames.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#333'
            }}>
              Bestehenden Plan auswählen (neue Version wird erstellt) oder <br></br>'Neuen Plan erstellen' wählen, um eine Kopie mit neuem Namen zu speichern:
            </label>
            <select
              value={selectedExistingPlan}
              onChange={(e) => {
                setSelectedExistingPlan(e.target.value);
                if (e.target.value) {
                  setPlanName('');
                }
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading ? '#f5f5f5' : 'white'
              }}
              disabled={loading}
            >
              <option value="">-- Neuen Plan erstellen --</option>
              {planNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Plan-Name (nur wenn kein bestehender Plan gewählt) */}
        {(!selectedExistingPlan || isUpdate) && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#333'
            }}>
              {isUpdate ? 'Plan-Name *' : 'Neuer Plan-Name *'}
            </label>
            <input
              type="text"
              value={planName}
              onChange={(e) => {
                const newValue = e.target.value;
                setPlanName(newValue);
                
                // Zeige Warnung wenn "unbenannt" eingegeben wird
                setShowNameWarning(newValue.trim().toLowerCase() === 'unbenannt');
                
                if (newValue && selectedExistingPlan) {
                  setSelectedExistingPlan('');
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder="Neuer Planname (z.B. Mathematik Semester 1)"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            
            {/* Sofortige Warnung bei "unbenannt" */}
            {showNameWarning && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#fff3e0',
                border: '1px solid #ff9800',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#f57c00',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ⚠️ Der Name "unbenannt" ist reserviert. Bitte wählen Sie einen anderen Namen.
              </div>
            )}
          </div>
        )}

        {/* Plan-Beschreibung */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '6px', 
            fontSize: '14px', 
            fontWeight: '500',
            color: '#333'
          }}>
            Beschreibung (optional)
          </label>
          <textarea
            value={planDescription}
            onChange={(e) => setPlanDescription(e.target.value)}
            placeholder="Kurze Beschreibung des Plans..."
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              minHeight: '80px',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Zeitliche Verschiebung */}
        {!isUpdate && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: selectedExistingPlan ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', color: selectedExistingPlan ? '#999' : '#333', marginBottom: (enableTimeShift && !selectedExistingPlan) ? '12px' : '0' }}>
              <input
                type="checkbox"
                checked={enableTimeShift}
                onChange={(e) => !selectedExistingPlan && setEnableTimeShift(e.target.checked)}
                disabled={selectedExistingPlan}
                style={{ width: '16px', height: '16px', cursor: selectedExistingPlan ? 'not-allowed' : 'pointer' }}
              />
              Zeitlich verschieben
              {selectedExistingPlan && (
                <span style={{ fontSize: '11px', color: '#999', fontWeight: '400', fontStyle: 'italic' }}>
                  (nur bei neuem Plan)
                </span>
              )}
            </label>
            
            {enableTimeShift && !selectedExistingPlan && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Startdatum (aktuell)
                  </label>
                  <input
                    type="date"
                    value={timeShiftStartDate}
                    onChange={(e) => setTimeShiftStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Zieldatum (neu)
                  </label>
                  <input
                    type="date"
                    value={timeShiftTargetDate}
                    onChange={(e) => setTimeShiftTargetDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        <div style={{
          padding: '12px',
          backgroundColor: selectedExistingPlan ? '#fff3e0' : '#e3f2fd',
          border: `1px solid ${selectedExistingPlan ? '#ff9800' : '#2196f3'}`,
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '12px', color: selectedExistingPlan ? '#f57c00' : '#1976d2', fontWeight: '500' }}>
            {selectedExistingPlan 
              ? `📝 Eine neue Version von "${selectedExistingPlan}" wird erstellt. Maximal 10 Versionen werden gespeichert.`
              : '💡 Tipp: Der Plan wird in Ihrem persönlichen Bereich gespeichert und ist nur für Sie sichtbar.'
            }
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            style={{
              padding: '10px 20px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: (!selectedExistingPlan && (!planName.trim() || planName.trim().toLowerCase() === 'unbenannt')) ? '#ccc' : '#2196f3',
              color: 'white',
              cursor: (!selectedExistingPlan && (!planName.trim() || planName.trim().toLowerCase() === 'unbenannt')) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={handleSave}
            disabled={!selectedExistingPlan && (!planName.trim() || planName.trim().toLowerCase() === 'unbenannt')}
          >
            <FiSave />
            {isUpdate ? 'Aktualisieren' : 'Speichern'}
          </button>
        </div>
        
        <div style={{ 
          marginTop: '12px', 
          fontSize: '12px', 
          color: '#999', 
          textAlign: 'center' 
        }}>
          Strg + Enter zum schnellen Speichern
        </div>
      </div>
    </div>
  );
};

export default DatabaseSaveDialog;